import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import type { FormWorker } from '@feature/dynamic_form/form_manager/form_worker';
import type { FormApiModel } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BookingScreen, useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { type BookingBasket, useBookingBasket } from '../../hooks/use_booking_basket';

interface AsyncState<T> {
	value: T | null;
	status: 'pending' | 'accessible' | 'error' | 'idle';
}

export function useScreenForms() {
	const viewBooking = useBooking();
	const bookingBasket = useBookingBasket();
	const { getBasket, formManager, restoreBasket } = bookingBasket;
	const analyticService = useResolve(AnalyticService);
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const screenAfterForms = featureUseRepresentatives ? BookingScreen.Access : BookingScreen.Conclusion;
	const [forms, setForms] = useState<AsyncState<FormApiModel[]>>({ value: null, status: 'pending' });
	const [formWorker, setFormWorker] = useState<AsyncState<Readonly<FormWorker>>>({ value: null, status: 'pending' });
	const tempDisposers = useRef<(() => void)[]>([]);
	const basketRef = useRef<BookingBasket | null>(getBasket());
	const hasSetupRef = useRef(false);

	const getNextFormId = useCallback(
		(currentFormId: number): number | null => {
			const currentFormIndex = bookingBasket.basketForms.findIndex((form) => form.Id === currentFormId);
			if (currentFormIndex === -1) return null;
			return bookingBasket.basketForms[currentFormIndex + 1]?.Id ?? null;
		},
		[bookingBasket.basketForms],
	);

	const loadSingleForm = useCallback(
		async (formId: number): Promise<void> => {
			if (basketRef.current == null) {
				setFormWorker({ value: null, status: 'error' });
				return;
			}
			setFormWorker({ value: null, status: 'pending' });
			const worker = formManager.getFormByFormId(formId);
			if (worker == null) {
				setFormWorker({ value: null, status: 'error' });
				return;
			}
			tempDisposers.current.forEach((x) => x());
			tempDisposers.current = [
				worker.events.on('onCommit', () => {
					const nextFormId = getNextFormId(worker.formId);
					if (nextFormId != null) viewBooking.goToScreen(BookingScreen.Forms, { formId: nextFormId });
					else viewBooking.goToScreen(screenAfterForms);
				}),
				worker.events.on('onChangePage', (data) => {
					viewBooking.goToScreen(BookingScreen.Forms, { formId: worker.formId, formPage: data.newPagePosition });
				}),
			];
			setFormWorker({ value: worker, status: 'accessible' });
		},
		[formManager, getNextFormId, screenAfterForms, viewBooking.goToScreen],
	);

	useEffect(() => {
		if (hasSetupRef.current) return;

		analyticService.trackPageView({ customTitle: 'Booking: Dynamic forms' });

		const basket = basketRef.current;
		const flowData = viewBooking.flowData.status === 'accessible' ? viewBooking.flowData.value : null;
		const allowed =
			basket != null &&
			flowData != null &&
			getBasket().respondantType != null &&
			(!flowData.useReasonForVisits ||
				viewBooking.getFilteredReasonForVisits().length === 0 ||
				!!viewBooking.getSelectedReasonForVisit()) &&
			!!basket.timeSlotId;

		if (!allowed) {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Dynamic forms');
			viewBooking.goToScreen(BookingScreen.Location);
			return;
		}

		// Restore the basket exactly once. restoreBasket() re-locks the time slot and
		// repopulates basketForms; since basketForms is a dependency below, letting it
		// run on every change caused repeated relock cycles that could clear the
		// selected time slot and bounce the user out of the flow (TVC-1243).
		hasSetupRef.current = true;

		(async () => {
			const restoreResult = await restoreBasket();
			if (restoreResult.err) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingDynamicForms);
				viewBooking.goToScreen(BookingScreen.Error);
				return;
			}
			const restoredForms = bookingBasket.basketForms;
			if (restoredForms.length < 1) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NoDynamicForms);
				viewBooking.goToScreen(screenAfterForms, { replace: true });
				return;
			}
			setForms({ value: restoredForms, status: 'accessible' });
			analyticService.trackEvent(
				AnalyticCategories.Booking,
				ViewBookingAnalyticActions.NumberOfDynamicForms,
				` ${restoredForms.length}`,
			);
		})();
	}, [
		analyticService,
		getBasket,
		bookingBasket.basketForms,
		restoreBasket,
		screenAfterForms,
		viewBooking.flowData,
		viewBooking.getFilteredReasonForVisits,
		viewBooking.getSelectedReasonForVisit,
		viewBooking.goToScreen,
	]);

	// Dispose the form event listeners only when leaving the forms screen.
	// Keeping this separate from the data-loading effect above prevents its
	// volatile dependencies from tearing the listeners down mid-flow (TVC-1243).
	useEffect(() => () => tempDisposers.current.forEach((x) => x()), []);

	return { forms, formWorker, loadSingleForm, getNextFormId };
}
